import React, { useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginWithPassword, resendVerification } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { loginRequest } from "../msalConfig";
import Logo from "../components/Logo";
import BrandLockup from "../components/BrandLockup";
import { useIsMobile } from "../hooks/useIsMobile";
import PasswordInput from "../components/PasswordInput";
import { getErrorMessage } from "../lib/errors";
import { startGoogleLogin } from "../services/googleAuth";
import { safeNextPath, withNext } from "../lib/redirects";
import { ACCOUNT_RECOVERY_DAYS, DELETED_ACCOUNT_CODE, SUPPORT_EMAIL } from "../lib/accountDeletion";
import { usePageMeta } from "../lib/pageMeta";

/* ─── Brand palette ──────────────────────────────────────────────────────── */
const C = {
  bg: "#FBFBF9", card: "#FFFFFF", surface: "#EEF2F2",
  ocean: "#1F7D91", oceanDeep: "#2A5A66", oceanDark: "#16313A",
  coral: "#E3704F", ink: "#18211C", muted: "#5D635F", soft: "#8A877F",
  border: "#E6E5E1", line: "#ECECEA", success: "#3FBF7F",
  display: "'Plus Jakarta Sans', system-ui, sans-serif",
  stat: "'Sora', sans-serif",
};

/* ─── Resume + AI illustration (SVG, used in left panel) ────────────────── */
const ResumeIllustration = () => (
  <svg viewBox="0 0 380 300" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ width: "100%", maxWidth: "380px", marginTop: "32px" }}
    aria-hidden="true"
  >
    <rect x="28" y="40" width="168" height="210" rx="9" fill="rgba(244,247,247,0.12)" transform="rotate(-7 28 40)" />
    <rect x="52" y="24" width="168" height="212" rx="10" fill="rgba(255,255,255,0.94)" />
    <rect x="52" y="24" width="168" height="48" rx="10" fill="rgba(31,125,145,0.16)" />
    <rect x="52" y="60" width="168" height="12" fill="rgba(31,125,145,0.16)" />
    <circle cx="83" cy="50" r="16" fill="rgba(31,125,145,0.34)" />
    <circle cx="83" cy="45" r="5" fill="rgba(31,125,145,0.72)" />
    <path d="M72 60 Q72 54 83 54 Q94 54 94 60 Z" fill="rgba(31,125,145,0.72)" />
    <rect x="107" y="43" width="82" height="7" rx="3.5" fill="rgba(42,90,102,0.55)" />
    <rect x="107" y="55" width="58" height="5" rx="2.5" fill="rgba(42,90,102,0.28)" />
    <rect x="70" y="88" width="32" height="5" rx="2.5" fill="rgba(227,112,79,0.55)" />
    <rect x="70" y="100" width="130" height="4" rx="2" fill="rgba(24,33,28,0.10)" />
    <rect x="70" y="109" width="105" height="4" rx="2" fill="rgba(24,33,28,0.10)" />
    <rect x="70" y="118" width="128" height="4" rx="2" fill="rgba(24,33,28,0.10)" />
    <rect x="70" y="142" width="40" height="5" rx="2.5" fill="rgba(227,112,79,0.55)" />
    <rect x="70" y="154" width="120" height="4" rx="2" fill="rgba(24,33,28,0.10)" />
    <rect x="70" y="163" width="90" height="4" rx="2" fill="rgba(24,33,28,0.10)" />
    <rect x="70" y="186" width="48" height="18" rx="9" fill="rgba(31,125,145,0.12)" />
    <rect x="123" y="186" width="42" height="18" rx="9" fill="rgba(227,112,79,0.16)" />
    <rect x="170" y="186" width="34" height="18" rx="9" fill="rgba(24,33,28,0.06)" />
    <rect x="182" y="84" width="158" height="160" rx="12" fill="rgba(22,49,58,0.92)" />
    <rect x="182" y="84" width="158" height="160" rx="12" stroke="rgba(31,125,145,0.6)" strokeWidth="1.5" />
    <text x="261" y="106" textAnchor="middle" fontFamily="Sora, system-ui" fontSize="8" fontWeight="700" letterSpacing="1.5" fill="rgba(63,191,127,0.9)">AI ANALYSIS</text>
    <text x="261" y="144" textAnchor="middle" fontFamily="Sora, system-ui" fontSize="38" fontWeight="700" fill="#3FBF7F">97</text>
    <text x="261" y="158" textAnchor="middle" fontFamily="Sora, system-ui" fontSize="7.5" letterSpacing="1" fill="rgba(244,247,247,0.45)">SCORE / 100</text>
    <g>
      <rect x="197" y="174" width="124" height="6" rx="3" fill="rgba(244,247,247,0.08)" /><rect x="197" y="174" width="108" height="6" rx="3" fill="rgba(31,125,145,0.85)" />
      <rect x="197" y="187" width="124" height="6" rx="3" fill="rgba(244,247,247,0.08)" /><rect x="197" y="187" width="88" height="6" rx="3" fill="rgba(31,125,145,0.85)" />
      <rect x="197" y="200" width="124" height="6" rx="3" fill="rgba(244,247,247,0.08)" /><rect x="197" y="200" width="96" height="6" rx="3" fill="rgba(31,125,145,0.85)" />
      <rect x="197" y="213" width="124" height="6" rx="3" fill="rgba(244,247,247,0.08)" /><rect x="197" y="213" width="122" height="6" rx="3" fill="rgba(31,125,145,0.85)" />
    </g>
    <rect x="192" y="29" width="84" height="30" rx="15" fill="#2FA56B" />
    <text x="234" y="48" textAnchor="middle" fontFamily="Plus Jakarta Sans, system-ui" fontSize="11" fontWeight="700" fill="#fff">✓ 98% Match</text>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const LoginNew = () => {
  usePageMeta();
  const { instance, inProgress } = useMsal();
  const navigate   = useNavigate();
  const location   = useLocation();
  const isMobile   = useIsMobile();
  const [searchParams] = useSearchParams();
  // Set when the visitor was sent here mid-flow (e.g. picking a plan on the
  // pricing page while signed out) — that destination wins over the dashboard
  // once they're in. Sanitised: an off-site value is dropped, not followed.
  const nextPath   = safeNextPath(searchParams.get('next'));
  const { setAuthFromLoginResponse } = useAuth();

  const [formData,        setFormData]        = useState({ email: "", password: "" });
  const [errors,          setErrors]          = useState({});
  const [isLoading,       setIsLoading]       = useState(false);
  const [rememberMe,      setRememberMe]      = useState(true);
  const [showVerifyNotice, setShowVerifyNotice] = useState(Boolean(location.state?.registered));
  // Set when login is refused specifically because the address is unconfirmed, so the way
  // out is offered right where the user hit the wall.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | failed

  // Redirecting an already-authenticated user is handled by <GuestRoute> in App.js.

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name] || errors.general) setErrors({});
    // Editing the form invalidates the previous attempt, so retract its verification offer.
    if (needsVerification) { setNeedsVerification(false); setResendState("idle"); }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);
    try {
      const data = await loginWithPassword(formData.email, formData.password, rememberMe);
      if (data?.user) setAuthFromLoginResponse(data.user);
      if (data?.user?.role === "global_admin") {
        navigate("/global-admin");
      } else {
        navigate(nextPath || "/dashboard");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setErrors({ general: "Incorrect email or password." });
      } else if (error.response?.data?.detail === DELETED_ACCOUNT_CODE) {
        // Someone whose account is inside its recovery window. Their password is not the
        // problem, so saying "incorrect" or the generic "inactive" would send them to the
        // reset flow, which cannot help. The deadline itself is deliberately not shown --
        // this check runs before the password is verified, so it answers anyone who types
        // the address. It went to the account's own inbox instead.
        setErrors({
          general: `This account has been deleted. Contact ${SUPPORT_EMAIL} to restore it. Deleted accounts are recoverable for ${ACCOUNT_RECOVERY_DAYS} days.`,
        });
      } else if (error.response?.headers?.["x-email-verification-required"]) {
        // The backend has always sent this header with its 403, and nothing read it. The
        // resend control lived only on the VerifyEmail page -- reachable from a
        // verification link, which is exactly what someone in this state never received.
        // Without an offer here, a correct password plus an unverified address is a dead
        // end with no way out of the UI.
        setNeedsVerification(true);
        setErrors({ general: getErrorMessage(error, "Please confirm your email address before signing in.") });
      } else {
        setErrors({ general: getErrorMessage(error, "Authentication failed. Please try again.") });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email || resendState === "sending") return;
    setResendState("sending");
    try {
      await resendVerification(formData.email);
      // Deliberately unconditional wording: /auth/resend-verification returns the same
      // response whether or not the address exists, to avoid confirming who has an account.
      setResendState("sent");
    } catch {
      setResendState("failed");
    }
  };

  const handleMicrosoftLogin = async () => {
    if (inProgress !== InteractionStatus.None) return;
    setIsLoading(true);
    const doRedirect = async () => instance.loginRedirect(loginRequest);
    try {
      await doRedirect();
    } catch (err) {
      if (err.errorCode === "interaction_in_progress") {
        Object.keys(sessionStorage)
          .filter(k => k.includes("interaction.status"))
          .forEach(k => sessionStorage.removeItem(k));
        try {
          await doRedirect();
          return;
        } catch {
          setErrors({ general: "Microsoft login failed. Please try again." });
        }
      } else {
        setErrors({ general: "Microsoft login failed. Please try again." });
      }
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => startGoogleLogin();

  /* ── Styles ────────────────────────────────────────────────────────────── */
  const inputStyle = {
    width: "100%",
    padding: "0.75rem 1rem 0.75rem 2.75rem",
    border: `1.5px solid ${C.border}`,
    borderRadius: "8px",
    fontSize: "0.9rem",
    color: C.ink,
    backgroundColor: C.card,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 180ms",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "0.45rem",
  };

  const socialBtnStyle = {
    flex: 1,
    padding: "0.65rem 0",
    border: `1.5px solid ${C.border}`,
    borderRadius: "8px",
    backgroundColor: C.card,
    color: C.ink,
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontFamily: "inherit",
    transition: "border-color 180ms, background-color 180ms",
  };

  const iconWrap = {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: C.soft,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.05fr 1fr",
      minHeight: "100vh",
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    }}>

      {/* ── Left: brand panel (deep teal) ─────────────────────────────────── */}
      <section style={{
        position: "relative",
        overflow: "hidden",
        display: isMobile ? "none" : "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "clamp(40px, 5vw, 64px)",
        background: "radial-gradient(120% 90% at 12% 8%, #2A5A66 0%, #16313A 100%)",
      }}>
        {/* Decorative blobs */}
        <div style={{
          position: "absolute", top: "-12%", left: "-8%",
          width: "46%", height: "46%", borderRadius: "50%",
          background: "rgba(255,255,255,0.05)", filter: "blur(70px)",
          zIndex: 0,
        }} />
        <div style={{
          position: "absolute", bottom: "-8%", right: "-6%",
          width: "42%", height: "42%", borderRadius: "50%",
          background: "rgba(227,112,79,0.16)", filter: "blur(60px)",
          zIndex: 0,
        }} />

        {/* Logo at top */}
        <div
          onClick={() => navigate("/")}
          style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative", zIndex: 1 }}
        >
          <BrandLockup height={32} tone="dark" />
        </div>

        {/* Body: headline + lead + illustration */}
        <div style={{ position: "relative", zIndex: 1, maxWidth: "32rem" }}>
          <h1 style={{
            fontSize: "2.4rem", fontWeight: 800, color: "#fff",
            margin: "0 0 0.85rem", lineHeight: 1.2,
            fontFamily: C.display,
          }}>
            AI-Powered<br />Resume Intelligence
          </h1>
          <p style={{
            fontSize: "0.92rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, margin: 0,
          }}>
            Upload resumes, define your criteria, and get ranked candidates in seconds. Powered by Azure GPT-4 with detailed reasoning for every decision.
          </p>

          <ResumeIllustration />
        </div>

        {/* Stats at bottom */}
        <div style={{ display: "flex", gap: "clamp(24px, 4vw, 48px)", position: "relative", zIndex: 1 }}>
          {[
            { val: "14.2k", label: "Resumes" },
            { val: "<1s",   label: "Speed / Retrieval" },
            { val: "98%",   label: "Accuracy" },
          ].map(({ val, label }) => (
            <div key={label}>
              <div style={{ fontSize: "1.9rem", fontWeight: 700, color: "#fff", fontFamily: C.stat, lineHeight: 1.1 }}>
                {val}
              </div>
              <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 3 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Right: form panel (off-white) ─────────────────────────────────── */}
      <section style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? "2rem 1.25rem" : "clamp(28px, 4vw, 48px) clamp(20px, 3vw, 32px)",
        background: C.bg,
      }}>
        <div style={{ width: "100%", maxWidth: "26rem" }}>

          {/* Back to home */}
          <button
            type="button"
            onClick={() => navigate("/")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: C.muted, fontSize: "0.75rem", fontWeight: 600,
              fontFamily: "inherit", marginBottom: "1.25rem",
              transition: "color 180ms",
            }}
            onMouseEnter={e => (e.currentTarget.style.color = C.ocean)}
            onMouseLeave={e => (e.currentTarget.style.color = C.muted)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to home
          </button>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "14px" }}>
              <Logo size={52} variant="ocean" />
            </div>
            <h1 style={{
              fontSize: "1.65rem", fontWeight: 800, color: C.ink,
              margin: "0 0 6px", fontFamily: C.display,
            }}>
              Sign in to TalentFora
            </h1>
            <p style={{ fontSize: "0.84rem", color: C.muted, margin: 0 }}>
              Sign in to continue screening resumes
            </p>
          </div>

          {/* Card */}
          <div style={{
            backgroundColor: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 14,
            padding: isMobile ? "1.35rem" : "1.85rem",
            boxShadow: "0 6px 32px rgba(31,125,145,0.07)",
          }}>

            {/* Post-registration verify-email notice */}
            {showVerifyNotice && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "12px 14px", borderRadius: 8, marginBottom: "1.25rem",
                background: "rgba(63,191,127,0.08)",
                border: "1px solid rgba(47,165,107,0.3)",
                color: "#2E8F5E", fontSize: "0.82rem", lineHeight: 1.5,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 5L2 7" />
                </svg>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontWeight: 700 }}>Account created.</strong> Check your inbox to verify your email before signing in.
                </div>
                <button
                  type="button"
                  onClick={() => setShowVerifyNotice(false)}
                  aria-label="Dismiss"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2E8F5E", padding: 0, lineHeight: 1, opacity: 0.7 }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Login refused because the address is unconfirmed — offer the way out here,
                since the only other resend control is behind a link they never got. */}
            {needsVerification && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "12px 14px", borderRadius: 8, marginBottom: "1.25rem",
                background: "rgba(212,160,39,0.09)",
                border: "1px solid rgba(212,160,39,0.32)",
                color: "#8A6410", fontSize: "0.82rem", lineHeight: 1.5,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 5L2 7" />
                </svg>
                <div style={{ flex: 1 }}>
                  {resendState === "sent" ? (
                    <>
                      <strong style={{ fontWeight: 700 }}>Check your inbox.</strong> If an account exists for
                      that address, a new confirmation link is on its way. It expires in 24 hours.
                    </>
                  ) : (
                    <>
                      <strong style={{ fontWeight: 700 }}>Your email isn’t confirmed yet.</strong> You need to
                      open the confirmation link before signing in.
                      {resendState === "failed" && (
                        <span style={{ display: "block", marginTop: 4 }}>
                          That didn’t go through. Please try again in a moment.
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendState === "sending" || !formData.email}
                        style={{
                          display: "block", marginTop: 7, background: "none", border: "none", padding: 0,
                          fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700,
                          color: "#8A6410", textDecoration: "underline",
                          cursor: resendState === "sending" || !formData.email ? "default" : "pointer",
                          opacity: resendState === "sending" || !formData.email ? 0.6 : 1,
                        }}
                      >
                        {resendState === "sending" ? "Sending…" : "Send me a new confirmation link"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* SSO row */}
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.25rem" }}>
              <button style={socialBtnStyle} onClick={handleGoogleLogin} disabled={isLoading}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.35 11.1H12.18V13.83H18.69C18.36 17.64 15.19 19.27 12.19 19.27C8.36 19.27 5 16.25 5 12C5 7.9 8.2 4.73 12.2 4.73C15.29 4.73 17.1 6.7 17.1 6.7L19 4.72C19 4.72 16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12C2.03 17.05 6.16 22 12.25 22C17.6 22 21.5 18.33 21.5 12.91C21.5 11.76 21.35 11.1 21.35 11.1Z" />
                </svg>
                Google
              </button>
              <button style={socialBtnStyle} onClick={handleMicrosoftLogin} disabled={isLoading}>
                <svg width="17" height="17" viewBox="0 0 21 21" fill="currentColor">
                  <path d="M1 1H10V10H1V1Z" fill="#F25022" />
                  <path d="M11 1H20V10H11V1Z" fill="#7FBA00" />
                  <path d="M1 11H10V20H1V11Z" fill="#00A4EF" />
                  <path d="M11 11H20V20H11V11Z" fill="#FFB900" />
                </svg>
                Microsoft
              </button>
            </div>

            {/* Or divider */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: "1.25rem",
              fontFamily: C.display, fontWeight: 600,
              fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: C.soft,
            }}>
              <div style={{ flex: 1, height: 1, background: C.border }} />
              Or sign in with
              <div style={{ flex: 1, height: 1, background: C.border }} />
            </div>

            {/* Form */}
            <form onSubmit={handleEmailLogin}>
              {errors.general && (
                <div style={{
                  padding: "0.75rem 1rem", borderRadius: 8, marginBottom: "1rem",
                  background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)",
                  color: "#dc2626", fontSize: "0.82rem", textAlign: "center",
                }}>
                  {errors.general}
                </div>
              )}

              {/* Email */}
              <div style={{ marginBottom: "1.1rem" }}>
                <label style={labelStyle} htmlFor="email">Email Address</label>
                <div style={{ position: "relative" }}>
                  <span style={iconWrap}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                  </span>
                  <input
                    id="email" name="email" type="email"
                    placeholder="you@company.com"
                    style={inputStyle}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                    onFocus={e  => (e.target.style.borderColor = C.ocean)}
                    onBlur={e   => (e.target.style.borderColor = C.border)}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: "1.25rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.45rem" }}>
                  <label style={{ ...labelStyle, margin: 0 }} htmlFor="password">Password</label>
                  <Link to="/forgot-password" style={{
                    fontSize: "0.72rem", color: C.ocean, textDecoration: "none",
                    fontWeight: 600, letterSpacing: "0.04em",
                  }}>
                    Forgot password?
                  </Link>
                </div>
                <div style={{ position: "relative" }}>
                  <span style={iconWrap}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <PasswordInput
                    id="password"
                    name="password"
                    placeholder="••••••••••"
                    style={inputStyle}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    onFocus={e => (e.target.style.borderColor = C.ocean)}
                    onBlur={e  => (e.target.style.borderColor = C.border)}
                  />
                </div>
              </div>

              {/* Remember me */}
              <label style={{
                display: "flex", alignItems: "center", gap: 8,
                marginBottom: "1.25rem", cursor: "pointer",
                fontSize: "0.8rem", color: C.muted, userSelect: "none",
              }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                  style={{ width: 16, height: 16, accentColor: C.ocean, cursor: "pointer" }}
                />
                Keep me signed in
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !formData.email || !formData.password}
                style={{
                  width: "100%", padding: "0.85rem",
                  backgroundColor: isLoading || !formData.email || !formData.password ? C.border : C.ocean,
                  color: isLoading || !formData.email || !formData.password ? C.muted : "#fff",
                  border: "none", borderRadius: 8,
                  fontSize: "0.9rem", fontWeight: 700,
                  cursor: isLoading || !formData.email || !formData.password ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "background-color 200ms",
                  fontFamily: "inherit",
                }}
              >
                {isLoading ? (
                  <>
                    <span style={{
                      display: "inline-block", width: 16, height: 16,
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#fff", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    Signing in&hellip;
                  </>
                ) : (
                  <>
                    Sign in
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            <p style={{ textAlign: "center", fontSize: "0.82rem", color: C.muted, marginTop: "1.25rem", marginBottom: 0 }}>
              Don&apos;t have an account?{" "}
              <Link to={withNext('/register', nextPath)} style={{ color: C.ocean, fontWeight: 700, textDecoration: "none" }}>
                Create an account
              </Link>
            </p>
          </div>

          {/* Legal links */}
          <div style={{
            textAlign: "center", marginTop: "1.5rem",
            fontSize: "0.72rem", color: C.soft, letterSpacing: "0.04em",
          }}>
            <Link to="/privacy" style={{ color: C.muted, textDecoration: "none" }}>Privacy Policy</Link>
            <span style={{ margin: "0 0.5rem" }}>·</span>
            <Link to="/terms" style={{ color: C.muted, textDecoration: "none" }}>Terms &amp; Conditions</Link>
          </div>

        </div>
      </section>

    </div>
  );
};

export default LoginNew;
